import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';

import { appConfig } from 'brand-config';
import { FormField } from 'core/components/form';
import Section from 'core/components/section';
import useSession from 'core/hooks/use-session';

// CFM auto Underwriting Adjustment (#35787). The CFM ClarionDoor auto rater already applies
// auto.adjustmentFactor as premium_modifier on every vehicle and trailer, defaulting to 1 when
// unset (#31782) — this section is the first staff UI that writes it. Titled "Policy-level
// Adjustment" to match the other LOBs; underwriter-only via the same permission that gates farm's
// policy-level adjustment, and brand-gated so it only renders where the rater reads the field.
const PolicyLevelAdjustment = ({ disabled }) => {
  const { canEditFarmPolicyAdjustment } = useSession();
  const { setFieldValue } = useFormikContext();

  if (!appConfig?.featureFlags?.autoPolicyLevelAdjustment || !canEditFarmPolicyAdjustment) {
    return null;
  }

  // Clearing the numeric input emits undefined, and JSON.stringify drops undefined keys from the
  // save payload — the backend merge would then silently keep the OLD multiplier while the UW
  // believes it was removed (the #34985 trap numeric.jsx documents). Persist an explicit null so a
  // cleared adjustment actually clears; the rater then applies 1.00.
  const persistClearedFactorAsNull = (value) => {
    if (value === undefined || value === '' || Number.isNaN(value)) {
      setFieldValue('auto.adjustmentFactor', null);
    }
  };

  return (
    <Section title="Policy-level Adjustment" type="SubSection">
      <Grid container spacing={4}>
        <FormField
          name="auto.adjustmentFactor"
          id="auto.adjustmentFactor"
          type="numeric"
          label="Underwriting Adjustment Factor"
          mode="light"
          format={{ decimalScale: 2, allowNegative: false }}
          xs={4}
          optional
          disabled={disabled}
          onChange={persistClearedFactorAsNull}
        />
        <FormField
          name="auto.adjustmentRationale"
          id="auto.adjustmentRationale"
          type="string"
          label="Rationale"
          mode="light"
          // Auto-growing rather than a fixed-height box: empty or short it is one line tall, the
          // same height as the factor input beside it, and it grows for the ~100-char sentence the
          // renewal rate cap writes here (#36285 — devlo on PR #36303). MUI v4.12 deprecates
          // `rowsMax` in favour of `maxRows`, which is what text-area.jsx uses.
          multiline
          maxRows={4}
          inputProps={{ maxLength: 1000 }}
          xs={8}
          optional
          disabled={disabled}
        />
      </Grid>
    </Section>
  );
};

PolicyLevelAdjustment.propTypes = {
  disabled: PropTypes.bool
};

PolicyLevelAdjustment.defaultProps = {
  disabled: false
};

export default PolicyLevelAdjustment;
